import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Code, Globe, Share, Lock, Copy, Check } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface ScrapeTemplate {
  id: string;
  name: string;
  platform?: string;
  description?: string;
  selector_rules: any;
  data_schema?: any;
  example_url?: string;
  is_public: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  platform: string;
  description: string;
  selector_rules: string;
  data_schema: string;
  example_url: string;
  is_public: boolean;
}

const ScrapeTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<ScrapeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [publicFilter, setPublicFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ScrapeTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    platform: 'general',
    description: '',
    selector_rules: '{\n  "container": ".item",\n  "fields": {\n    "title": "h2",\n    "content": "p",\n    "link": "a[href]"\n  }\n}',
    data_schema: '{\n  "title": "string",\n  "content": "string",\n  "link": "url"\n}',
    example_url: '',
    is_public: false
  });

  const platforms = [
    { value: 'general', label: '通用', color: 'gray' },
    { value: 'ecommerce', label: '电商', color: 'green' },
    { value: 'news', label: '新闻', color: 'blue' },
    { value: 'social', label: '社交', color: 'purple' },
    { value: 'forum', label: '论坛', color: 'orange' },
    { value: 'blog', label: '博客', color: 'pink' },
    { value: 'custom', label: '自定义', color: 'indigo' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, [currentPage, pageSize, searchTerm, platformFilter, publicFilter]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=template_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: currentPage,
          limit: pageSize,
          search: searchTerm || undefined,
          platform: platformFilter !== 'all' ? platformFilter : undefined,
          is_public: publicFilter !== 'all' ? (publicFilter === 'public') : undefined
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setTemplates(result.data.data || []);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalPages(result.data.pagination?.totalPages || 0);
      } else {
        console.error('获取采集模板失败:', result.error);
        toast.error('获取采集模板失败');
      }
    } catch (error) {
      console.error('获取采集模板失败:', error);
      toast.error('获取采集模板失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      // 验证JSON格式
      let selectorRules, dataSchema;
      try {
        selectorRules = JSON.parse(formData.selector_rules);
        dataSchema = formData.data_schema ? JSON.parse(formData.data_schema) : null;
      } catch (error) {
        toast.error('选择器规则或数据结构不是有效的JSON格式');
        return;
      }

      const requestData = {
        name: formData.name,
        platform: formData.platform,
        description: formData.description || null,
        selector_rules: selectorRules,
        data_schema: dataSchema,
        example_url: formData.example_url || null,
        is_public: formData.is_public
      };

      if (editingTemplate) {
        // 更新模板
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=template_update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: editingTemplate.id,
            ...requestData
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('模板更新成功');
      } else {
        // 创建模板
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=template_create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('模板创建成功');
      }

      await fetchTemplates();
      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      console.error('保存模板失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除模板「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=template_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('模板删除成功');
      await fetchTemplates();
    } catch (error) {
      console.error('删除模板失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleCopyRules = async (rules: any, id: string) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rules, null, 2));
      setCopiedId(id);
      toast.success('选择器规则已复制到剪贴板');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('复制失败');
    }
  };

  const handleEdit = (template: ScrapeTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      platform: template.platform || 'general',
      description: template.description || '',
      selector_rules: JSON.stringify(template.selector_rules, null, 2),
      data_schema: template.data_schema ? JSON.stringify(template.data_schema, null, 2) : '{}',
      example_url: template.example_url || '',
      is_public: template.is_public
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      platform: 'general',
      description: '',
      selector_rules: '{\n  "container": ".item",\n  "fields": {\n    "title": "h2",\n    "content": "p",\n    "link": "a[href]"\n  }\n}',
      data_schema: '{\n  "title": "string",\n  "content": "string",\n  "link": "url"\n}',
      example_url: '',
      is_public: false
    });
  };

  // 分页处理函数
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // 重置到第一页
  };

  // 搜索和筛选处理函数
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // 重置到第一页
  };

  const handlePlatformFilterChange = (value: string) => {
    setPlatformFilter(value);
    setCurrentPage(1); // 重置到第一页
  };

  const handlePublicFilterChange = (value: string) => {
    setPublicFilter(value);
    setCurrentPage(1); // 重置到第一页
  };

  // 由于现在使用服务端分页，不需要客户端过滤
  const filteredTemplates = templates;

  const getPlatformInfo = (platform: string) => {
    return platforms.find(p => p.value === platform) || platforms[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Code className="w-6 h-6 mr-2 text-blue-600" />
              采集模板管理
            </h2>
            <button
              onClick={() => {
                setEditingTemplate(null);
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加模板
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索模板名称、描述或平台..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={platformFilter}
              onChange={(e) => handlePlatformFilterChange(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部平台</option>
              {platforms.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>
            <select
              value={publicFilter}
              onChange={(e) => handlePublicFilterChange(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              <option value="public">公开模板</option>
              <option value="private">私有模板</option>
            </select>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value={10}>10条/页</option>
              <option value={20}>20条/页</option>
              <option value={50}>50条/页</option>
              <option value={100}>100条/页</option>
            </select>
          </div>

          {/* 数据统计信息 */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600">
              共 {totalCount} 条记录，第 {currentPage} 页，共 {totalPages} 页
            </div>
          </div>

          {/* 模板列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTemplates.map((template) => {
              const platformInfo = getPlatformInfo(template.platform || 'general');
              
              return (
                <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-${platformInfo.color}-100`}>
                        <Code className={`w-5 h-5 text-${platformInfo.color}-600`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${platformInfo.color}-100 text-${platformInfo.color}-800`}>
                            {platformInfo.label}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            template.is_public 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.is_public ? (
                              <><Share className="w-3 h-3 mr-1" />公开</>
                            ) : (
                              <><Lock className="w-3 h-3 mr-1" />私有</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCopyRules(template.selector_rules, template.id)}
                        className="text-gray-400 hover:text-gray-600"
                        title="复制选择器规则"
                      >
                        {copiedId === template.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-blue-600 hover:text-blue-900"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id, template.name)}
                        className="text-red-600 hover:text-red-900"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {template.description && (
                    <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                  )}

                  {template.example_url && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 mb-1">示例URL</div>
                      <div className="text-sm text-gray-700 flex items-center">
                        <Globe className="w-4 h-4 mr-1" />
                        <span className="truncate">{template.example_url}</span>
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 rounded-md p-3 mb-4">
                    <div className="text-xs text-gray-500 mb-2">选择器规则预览</div>
                    <pre className="text-xs text-gray-700 overflow-x-auto">
                      {JSON.stringify(template.selector_rules, null, 2).substring(0, 200)}
                      {JSON.stringify(template.selector_rules, null, 2).length > 200 && '...'}
                    </pre>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>创建时间: {new Date(template.created_at).toLocaleDateString()}</span>
                    <span>更新时间: {new Date(template.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredTemplates.length === 0 && !loading && (
            <div className="text-center py-12">
              <Code className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无采集模板数据</p>
            </div>
          )}

          {/* 分页控件 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  首页
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
              </div>

              <div className="flex items-center space-x-2">
                {/* 页码按钮 */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="start-ellipsis" className="px-3 py-2 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          i === currentPage
                            ? 'text-blue-600 bg-blue-50 border border-blue-300'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="end-ellipsis" className="px-3 py-2 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  末页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTemplate ? '编辑采集模板' : '添加采集模板'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      模板名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入模板名称"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      平台类型
                    </label>
                    <select
                      value={formData.platform}
                      onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      {platforms.map(platform => (
                        <option key={platform.value} value={platform.value}>
                          {platform.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    模板描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入模板描述"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    示例URL
                  </label>
                  <input
                    type="url"
                    value={formData.example_url}
                    onChange={(e) => setFormData({ ...formData, example_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    选择器规则 (JSON格式) *
                  </label>
                  <textarea
                    required
                    value={formData.selector_rules}
                    onChange={(e) => setFormData({ ...formData, selector_rules: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='{"container": ".item", "fields": {"title": "h2", "content": "p"}}'
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    定义如何从页面中提取数据的选择器规则
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    数据结构 (JSON格式)
                  </label>
                  <textarea
                    value={formData.data_schema}
                    onChange={(e) => setFormData({ ...formData, data_schema: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='{"title": "string", "content": "string", "link": "url"}'
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    定义提取数据的字段类型和验证规则
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">公开此模板（其他用户可以使用）</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingTemplate(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapeTemplateManagement;